/**
 * Rotas geradas a partir do MODULES registry.
 * Substitui ~380 linhas de `<Route>` repetitivas em App.tsx — uma entrada por
 * módulo (Tribunais, Carreiras Policiais/Jurídicas, CNU, Bancário, Militares,
 * Residência, Colégios, Vestibulares).
 *
 * StudyPlan, QuestionDetail compartilhado e rotas bespoke continuam em App.tsx
 * pois usam componentes específicos por módulo.
 */
import { lazy, Fragment } from 'react';
import { Route } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ModuleProtectedRoute } from '@/components/ModuleProtectedRoute';
import { MODULES, type ModuleSlug, type ModuleConfig } from '@/lib/modules/registry';
import type { ModuleType } from '@/hooks/useModuleAccess';

const QuestionDetail = lazy(() => import('@/pages/QuestionDetail'));
const PomodoroStudy = lazy(() => import('@/pages/PomodoroStudy'));
const PomodoroHistory = lazy(() => import('@/pages/PomodoroHistory'));

// Os 9 page-sets clone. Cada um é um wrapper fino sobre os componentes
// genéricos em src/components/modules/*. Mantemos lazy() para preservar
// code-splitting por módulo.
type PageSet = {
  Dashboard: React.LazyExoticComponent<React.ComponentType<any>>;
  Questions: React.LazyExoticComponent<React.ComponentType<any>>;
  Simulations: React.LazyExoticComponent<React.ComponentType<any>>;
  CreateSimulation: React.LazyExoticComponent<React.ComponentType<any>>;
  Ranking: React.LazyExoticComponent<React.ComponentType<any>>;
  Settings: React.LazyExoticComponent<React.ComponentType<any>>;
  Analytics: React.LazyExoticComponent<React.ComponentType<any>>;
  Achievements: React.LazyExoticComponent<React.ComponentType<any>>;
  ErrorPatterns: React.LazyExoticComponent<React.ComponentType<any>>;
  SmartReview: React.LazyExoticComponent<React.ComponentType<any>>;
  WeakAreasStudy: React.LazyExoticComponent<React.ComponentType<any>>;
};

const MODULE_PAGES: Record<ModuleSlug, PageSet> = {
  tribunais: {
    Dashboard: lazy(() => import('@/pages/tribunais/TribunaisDashboard')),
    Questions: lazy(() => import('@/pages/tribunais/TribunaisQuestions')),
    Simulations: lazy(() => import('@/pages/tribunais/TribunaisSimulations')),
    CreateSimulation: lazy(() => import('@/pages/tribunais/TribunaisCreateSimulation')),
    Ranking: lazy(() => import('@/pages/tribunais/TribunaisRanking')),
    Settings: lazy(() => import('@/pages/tribunais/TribunaisSettings')),
    Analytics: lazy(() => import('@/pages/tribunais/TribunaisAnalytics')),
    Achievements: lazy(() => import('@/pages/tribunais/TribunaisAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/tribunais/TribunaisErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/tribunais/TribunaisSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/tribunais/TribunaisWeakAreasStudy')),
  },
  policiais: {
    Dashboard: lazy(() => import('@/pages/policiais/PoliciaisDashboard')),
    Questions: lazy(() => import('@/pages/policiais/PoliciaisQuestions')),
    Simulations: lazy(() => import('@/pages/policiais/PoliciaisSimulations')),
    CreateSimulation: lazy(() => import('@/pages/policiais/PoliciaisCreateSimulation')),
    Ranking: lazy(() => import('@/pages/policiais/PoliciaisRanking')),
    Settings: lazy(() => import('@/pages/policiais/PoliciaisSettings')),
    Analytics: lazy(() => import('@/pages/policiais/PoliciaisAnalytics')),
    Achievements: lazy(() => import('@/pages/policiais/PoliciaisAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/policiais/PoliciaisErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/policiais/PoliciaisSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/policiais/PoliciaisWeakAreasStudy')),
  },
  juridicas: {
    Dashboard: lazy(() => import('@/pages/juridicas/JuridicasDashboard')),
    Questions: lazy(() => import('@/pages/juridicas/JuridicasQuestions')),
    Simulations: lazy(() => import('@/pages/juridicas/JuridicasSimulations')),
    CreateSimulation: lazy(() => import('@/pages/juridicas/JuridicasCreateSimulation')),
    Ranking: lazy(() => import('@/pages/juridicas/JuridicasRanking')),
    Settings: lazy(() => import('@/pages/juridicas/JuridicasSettings')),
    Analytics: lazy(() => import('@/pages/juridicas/JuridicasAnalytics')),
    Achievements: lazy(() => import('@/pages/juridicas/JuridicasAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/juridicas/JuridicasErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/juridicas/JuridicasSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/juridicas/JuridicasWeakAreasStudy')),
  },
  cnu: {
    Dashboard: lazy(() => import('@/pages/cnu/CnuDashboard')),
    Questions: lazy(() => import('@/pages/cnu/CnuQuestions')),
    Simulations: lazy(() => import('@/pages/cnu/CnuSimulations')),
    CreateSimulation: lazy(() => import('@/pages/cnu/CnuCreateSimulation')),
    Ranking: lazy(() => import('@/pages/cnu/CnuRanking')),
    Settings: lazy(() => import('@/pages/cnu/CnuSettings')),
    Analytics: lazy(() => import('@/pages/cnu/CnuAnalytics')),
    Achievements: lazy(() => import('@/pages/cnu/CnuAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/cnu/CnuErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/cnu/CnuSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/cnu/CnuWeakAreasStudy')),
  },
  bancario: {
    Dashboard: lazy(() => import('@/pages/bancario/BancarioDashboard')),
    Questions: lazy(() => import('@/pages/bancario/BancarioQuestions')),
    Simulations: lazy(() => import('@/pages/bancario/BancarioSimulations')),
    CreateSimulation: lazy(() => import('@/pages/bancario/BancarioCreateSimulation')),
    Ranking: lazy(() => import('@/pages/bancario/BancarioRanking')),
    Settings: lazy(() => import('@/pages/bancario/BancarioSettings')),
    Analytics: lazy(() => import('@/pages/bancario/BancarioAnalytics')),
    Achievements: lazy(() => import('@/pages/bancario/BancarioAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/bancario/BancarioErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/bancario/BancarioSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/bancario/BancarioWeakAreasStudy')),
  },
  militares: {
    Dashboard: lazy(() => import('@/pages/militares/MilitaresDashboard')),
    Questions: lazy(() => import('@/pages/militares/MilitaresQuestions')),
    Simulations: lazy(() => import('@/pages/militares/MilitaresSimulations')),
    CreateSimulation: lazy(() => import('@/pages/militares/MilitaresCreateSimulation')),
    Ranking: lazy(() => import('@/pages/militares/MilitaresRanking')),
    Settings: lazy(() => import('@/pages/militares/MilitaresSettings')),
    Analytics: lazy(() => import('@/pages/militares/MilitaresAnalytics')),
    Achievements: lazy(() => import('@/pages/militares/MilitaresAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/militares/MilitaresErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/militares/MilitaresSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/militares/MilitaresWeakAreasStudy')),
  },
  residencia: {
    Dashboard: lazy(() => import('@/pages/residencia/ResidenciaDashboard')),
    Questions: lazy(() => import('@/pages/residencia/ResidenciaQuestions')),
    Simulations: lazy(() => import('@/pages/residencia/ResidenciaSimulations')),
    CreateSimulation: lazy(() => import('@/pages/residencia/ResidenciaCreateSimulation')),
    Ranking: lazy(() => import('@/pages/residencia/ResidenciaRanking')),
    Settings: lazy(() => import('@/pages/residencia/ResidenciaSettings')),
    Analytics: lazy(() => import('@/pages/residencia/ResidenciaAnalytics')),
    Achievements: lazy(() => import('@/pages/residencia/ResidenciaAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/residencia/ResidenciaErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/residencia/ResidenciaSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/residencia/ResidenciaWeakAreasStudy')),
  },
  colegios: {
    Dashboard: lazy(() => import('@/pages/colegios/ColegiosDashboard')),
    Questions: lazy(() => import('@/pages/colegios/ColegiosQuestions')),
    Simulations: lazy(() => import('@/pages/colegios/ColegiosSimulations')),
    CreateSimulation: lazy(() => import('@/pages/colegios/ColegiosCreateSimulation')),
    Ranking: lazy(() => import('@/pages/colegios/ColegiosRanking')),
    Settings: lazy(() => import('@/pages/colegios/ColegiosSettings')),
    Analytics: lazy(() => import('@/pages/colegios/ColegiosAnalytics')),
    Achievements: lazy(() => import('@/pages/colegios/ColegiosAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/colegios/ColegiosErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/colegios/ColegiosSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/colegios/ColegiosWeakAreasStudy')),
  },
  vestibulares: {
    Dashboard: lazy(() => import('@/pages/vestibulares/VestibularesDashboard')),
    Questions: lazy(() => import('@/pages/vestibulares/VestibularesQuestions')),
    Simulations: lazy(() => import('@/pages/vestibulares/VestibularesSimulations')),
    CreateSimulation: lazy(() => import('@/pages/vestibulares/VestibularesCreateSimulation')),
    Ranking: lazy(() => import('@/pages/vestibulares/VestibularesRanking')),
    Settings: lazy(() => import('@/pages/vestibulares/VestibularesSettings')),
    Analytics: lazy(() => import('@/pages/vestibulares/VestibularesAnalytics')),
    Achievements: lazy(() => import('@/pages/vestibulares/VestibularesAchievements')),
    ErrorPatterns: lazy(() => import('@/pages/vestibulares/VestibularesErrorPatterns')),
    SmartReview: lazy(() => import('@/pages/vestibulares/VestibularesSmartReview')),
    WeakAreasStudy: lazy(() => import('@/pages/vestibulares/VestibularesWeakAreasStudy')),
  },
};

function wrap(
  moduleKey: ModuleType,
  Element: React.LazyExoticComponent<React.ComponentType<any>>,
) {
  return (
    <ProtectedRoute>
      <ModuleProtectedRoute module={moduleKey}>
        <Element />
      </ModuleProtectedRoute>
    </ProtectedRoute>
  );
}

/**
 * Gera todas as rotas-padrão de um módulo clone.
 * O `module` enviado ao ModuleProtectedRoute usa `targetExamsKey` do registry
 * (já alinhado a useModuleAccess: 'tribunais', 'carreiras-juridicas', etc.).
 */
function moduleRoutes(config: ModuleConfig) {
  const pages = MODULE_PAGES[config.slug];
  const prefix = config.routePrefix;
  const moduleKey = config.targetExamsKey as ModuleType;

  return (
    <Fragment key={config.slug}>
      <Route path={`${prefix}/questions/:id`} element={wrap(moduleKey, QuestionDetail)} />
      <Route path={prefix} element={wrap(moduleKey, pages.Dashboard)} />
      <Route path={`${prefix}/questions`} element={wrap(moduleKey, pages.Questions)} />
      <Route path={`${prefix}/simulations`} element={wrap(moduleKey, pages.Simulations)} />
      <Route path={`${prefix}/simulations/create`} element={wrap(moduleKey, pages.CreateSimulation)} />
      <Route path={`${prefix}/ranking`} element={wrap(moduleKey, pages.Ranking)} />
      <Route path={`${prefix}/settings`} element={wrap(moduleKey, pages.Settings)} />
      <Route path={`${prefix}/analytics`} element={wrap(moduleKey, pages.Analytics)} />
      {config.sidebar.showSmartReview && (
        <Route path={`${prefix}/smart-review`} element={wrap(moduleKey, pages.SmartReview)} />
      )}
      {config.sidebar.showErrorPatterns && (
        <Route path={`${prefix}/error-patterns`} element={wrap(moduleKey, pages.ErrorPatterns)} />
      )}
      {config.sidebar.showWeakAreas && (
        <Route path={`${prefix}/weak-areas`} element={wrap(moduleKey, pages.WeakAreasStudy)} />
      )}
      {config.sidebar.showAchievements && (
        <Route path={`${prefix}/achievements`} element={wrap(moduleKey, pages.Achievements)} />
      )}
      {config.sidebar.showPomodoro && (
        <>
          <Route path={`${prefix}/pomodoro`} element={wrap(moduleKey, PomodoroStudy)} />
          <Route path={`${prefix}/pomodoro/history`} element={wrap(moduleKey, PomodoroHistory)} />
        </>
      )}
    </Fragment>
  );
}

/**
 * Retorna o array de <Route> de todos os módulos clone.
 * Uso em App.tsx: `{renderModuleRoutes()}` dentro de `<Routes>`.
 */
export function renderModuleRoutes() {
  return Object.values(MODULES).map(moduleRoutes);
}
